import { EVENT_COLORS, type CalendarEvent, type CalendarEventType, type LiveLesson } from '@/api/calendar';

/**
 * События календаря ученика одним списком — живые уроки, события платформы (в них сервер подмешивает разговорные
 * комнаты) и личные записи.
 *
 * ⚠️ ОДНА СБОРКА НА СТРАНИЦУ РАСПИСАНИЯ И НА ГЛАВНУЮ (02.10.2026, сверка `docs/CHECK-2026-10-01-CALENDAR.md` §2).
 * До этого страница собирала три источника сама, а недельные виджеты главной брали один — события платформы.
 * Ученик заводил личную запись, видел её в расписании и читал на главной «занятий нет». Точка на главной обязана
 * означать то же событие, что в расписании, поэтому список собирается здесь, а экраны только рисуют.
 */
export type CalItem = {
  id: string;
  title: string;
  description?: string;
  start: Date;
  kind: CalendarEventType | 'live_lesson';
  color: string;
  link?: string;
  durationMin?: number;
  /** id живого урока — по нему кнопка «Войти» ведёт в комнату урока. */
  lessonId?: string;
  /** Исходная личная запись — для правки и удаления. */
  personal?: CalendarEvent;
};

export const MS_DAY = 86_400_000;
const MS_HOUR = 3_600_000;

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function sameDay(a: Date, b: Date): boolean {
  return startOfDay(a).getTime() === startOfDay(b).getTime();
}

/** Понедельник недели, в которую попадает день. */
export function startOfWeek(d: Date): Date {
  const s = startOfDay(d);
  return new Date(s.getFullYear(), s.getMonth(), s.getDate() - ((s.getDay() + 6) % 7));
}

export type CalSources = {
  lessons?: LiveLesson[];
  platform?: CalendarEvent[];
  personal?: CalendarEvent[];
  /** Показывать ли живые уроки. */
  showLive: boolean;
  /** Показывать ли разговорные комнаты: нет — их нет ни строкой, ни точкой под днём. */
  showRooms: boolean;
};

export function buildCalItems({ lessons, platform, personal, showLive, showRooms }: CalSources): CalItem[] {
  const out: CalItem[] = [];
  if (showLive) {
    (lessons ?? []).forEach((l) =>
      out.push({
        id: `ll-${l.id}`,
        title: l.title,
        description: l.topic,
        start: new Date(l.scheduled_at),
        kind: 'live_lesson',
        color: EVENT_COLORS.live_lesson,
        link: l.meet_link,
        lessonId: l.id,
        durationMin: l.duration_min,
      }),
    );
  }
  (platform ?? []).forEach((e) => {
    // Строка «Speaking Room в 18:00», в которую нельзя войти, хуже её отсутствия: ученик решит, что сломалось.
    if (e.type === 'speaking_room' && !showRooms) return;
    out.push({
      id: `pe-${e.id}`,
      title: e.title,
      description: e.description,
      start: new Date(e.start_at),
      kind: e.type,
      color: EVENT_COLORS[e.type] ?? EVENT_COLORS.announcement,
      link: e.link,
    });
  });
  (personal ?? []).forEach((e) =>
    out.push({
      id: `ps-${e.id}`,
      title: e.title,
      description: e.description,
      start: new Date(e.start_at),
      kind: 'personal',
      color: EVENT_COLORS.personal,
      personal: e,
    }),
  );
  // Событие без времени начала в сетку не ставится: у него нет дня.
  return out
    .filter((x) => !Number.isNaN(x.start.getTime()))
    .sort((a, b) => a.start.getTime() - b.start.getTime());
}

export function itemsOn(items: CalItem[], day: Date): CalItem[] {
  return items.filter((x) => sameDay(x.start, day));
}

/** Событие уже началось. В списке дня такое остаётся, но приглушённым. */
export function isPast(item: CalItem, now: Date): boolean {
  return item.start.getTime() < now.getTime();
}

/** Сколько часов до срока считается «близко». */
export const DEADLINE_NEAR_HOURS = 48;

/**
 * Метка «дедлайн близко»: событие типа `deadline`, до которого осталось не больше 48 часов.
 * Прошедший срок метку не получает — она про то, что ещё можно успеть.
 */
export function deadlineNear(item: CalItem, now: Date): boolean {
  if (item.kind !== 'deadline') return false;
  const left = item.start.getTime() - now.getTime();
  return left > 0 && left <= DEADLINE_NEAR_HOURS * MS_HOUR;
}

/** Личные записи на сегодня — строки блока «Задачи на сегодня» на главной. */
export function todayPersonal(items: CalItem[], now: Date): CalItem[] {
  return items.filter((x) => x.kind === 'personal' && sameDay(x.start, now));
}

export type DayRows = {
  /** Строки под недельной полоской: сначала то, что впереди, потом уже начавшееся. */
  rows: CalItem[];
  /** В выбранном дне пусто, день не выбирали — показаны ближайшие события вообще. */
  fallback: boolean;
};

/**
 * Строки под недельной полоской главной.
 *
 * ⚠️ НАЧАВШЕЕСЯ СЕГОДНЯ НЕ ПРЯЧЕТСЯ, А УХОДИТ ВНИЗ СПИСКА (02.10.2026). Раньше компьютер выбрасывал всё, что
 * началось раньше текущей минуты: под днём стояла точка, а в списке было пусто — утреннюю запись ученик после
 * полудня уже не находил. Теперь событие остаётся, приглушённое (`isPast`). Впереди идущие стоят первыми, чтобы
 * три строки виджета не заняло то, куда идти поздно (ради этого прошедшее когда-то и отсекали, 07.09.2026).
 */
export function dayRows(items: CalItem[], shownDay: Date, picked: boolean, now: Date): DayRows {
  const day = itemsOn(items, shownDay);
  if (day.length > 0) {
    return { rows: [...day.filter((x) => !isPast(x, now)), ...day.filter((x) => isPast(x, now))], fallback: false };
  }
  // Выбранный пустой день честно пуст; невыбранный — ближайшее вообще, чтобы строка не была прочерком.
  if (picked) return { rows: [], fallback: false };
  return { rows: items.filter((x) => !isPast(x, now)), fallback: true };
}
